import React, { FunctionComponent, ReactElement } from 'react'
import Gradient from '@sport1/react-elements/Gradient'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import { ImageLabel } from '../../../BaseComponents/Image/Components'
import { ChannelProps, TeaserTypes } from '../../TeaserInterface'
import DurationLabel from '../../../BaseComponents/Labels/Duration'
import GalleryCountLabel from '../../../BaseComponents/Labels/GalleryCount'
import BigTeaserHeadlineFont from '../../../Theme/Fonts/BigTeaserHeadline'
import VerticalSpacer from '../../../Layouts/Spacer/Vertical'
import TeaserInfo from '../../../TeaserInfo'
import Image from '../../../BaseComponents/Image'
import { LabelProps } from '../../../BaseComponents/Labels/Types'

export type CarouselTeaserV2Props = LabelProps & {
    mainImageUrl?: string
    mainImageAlt?: string
    tag?: ChannelProps
    published?: string
    teaserType: TeaserTypes
    videoDuration?: string
    numberOfImages?: number
    headlineText: string
    onPress?: (type: TeaserTypes) => void
    /**
     * is used to render with different height
     *
     * @defaultValue 343:272
     */
    imageRatio?: '343:272' | '3:2'
}

type TeaserTypeExtraComponentProps = {
    teaserType: TeaserTypes
    videoDuration?: string
    numberOfImages?: number
}

const getTeaserTypeExtraComponent = ({
    teaserType,
    videoDuration,
    numberOfImages,
}: TeaserTypeExtraComponentProps): ReactElement | null => {
    if (teaserType === 'VIDEO' && videoDuration) {
        return <DurationLabel withIcon duration={videoDuration} />
    } else if (teaserType === 'GALLERY' && numberOfImages) {
        return <GalleryCountLabel numberOfImages={numberOfImages} />
    }
    return null
}

const CarouselTeaserV2: FunctionComponent<CarouselTeaserV2Props> = ({
    mainImageUrl,
    mainImageAlt,
    tag,
    published,
    teaserType,
    videoDuration,
    numberOfImages,
    labels,
    headlineText,
    onPress,
    imageRatio = '343:272',
}) => {
    const extraComponent = getTeaserTypeExtraComponent({
        teaserType,
        videoDuration,
        numberOfImages,
    })
    const onPressCallback = (): void => onPress && onPress(teaserType)
    return (
        <Touchable onPress={onPressCallback}>
            <Grid noExpand horizontal width="100%" backgroundColor="pearl">
                <Grid position="relative" marginX={5}>
                    <Image
                        imageUrl={mainImageUrl}
                        alt={mainImageAlt}
                        width="100%"
                        aspectRatio={imageRatio}
                        size="L"
                        radiusType="rounded"
                    />
                    <Grid
                        position="absolute"
                        top={0}
                        bottom={0}
                        left={0}
                        right={0}
                        borderRadius={5}
                        overflow="hidden"
                    >
                        <Grid width="100%" height="100%">
                            <Gradient alignBottom expand />
                        </Grid>
                    </Grid>
                    {labels ? <ImageLabel labels={labels} /> : null}
                    <Grid position="absolute" bottom={40} right={5} left={5}>
                        <BigTeaserHeadlineFont numberOfLines={3} color="pearl">
                            {headlineText}
                        </BigTeaserHeadlineFont>
                        <VerticalSpacer space="small" backgroundColor="TRANSPARENT" />
                        <Grid horizontal centerVertical>
                            <Grid>
                                <TeaserInfo
                                    imageUrl={tag?.imageUrl}
                                    alt={tag?.imageAlt}
                                    published={published}
                                    name={tag?.name}
                                    color="pearl"
                                    tintColor={tag?.imageTintColor}
                                />
                            </Grid>
                            {extraComponent}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Touchable>
    )
}

export default CarouselTeaserV2
